import React, { useState } from "react";
import PropTypes from "prop-types";
import { Table, Tag , Form, InputNumber, Space, Button, notification} from "antd";
import styled from "styled-components";
import { appConstant, PaymentStatus, THEME } from "Util/constants";
import TableFilter from "./TableFilter";
import _ from "lodash";
import { useRecoilState } from "recoil";
import { speciesSearchAtom } from "Store/procurementAtom";
import { UPDATE_INVOICE, INSERT_INVOICE_HISTORY } from 'Util/mutations';
import { useMutation } from '@apollo/client';
import moment from "moment";
import ActionDropdown from "./ActionDropdown";
import leadingZeroes from 'leading-zeroes';
import User from "Hooks/user";
import { CloseOutlined, EditOutlined } from "@ant-design/icons";

const Container = styled.div`
	.ant-table-thead {
		.ant-table-cell {
			color: #999;
			font-weight: bold;
		}
	}

	.ant-table-tbody > tr:hover > td {
		background-color: "none";
	}
	.ant-table-tbody > tr.selected > td {
		background-color: ${THEME.colors.primary};
	}
  .shorten{
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const EditableRow = ({ ...props }) => {
  return <tr {...props} />;
};

const EditableCell = ({
  editable,
  dataIndex,
  record,
  editing,
  children,
  ...restProps
}) => {

  return (
    <td {...restProps}>
      {editable && editing ? (
        <Form.Item
          name={dataIndex}
          style={{ margin: 0 }}
        >
          <InputNumber style={{ width: "100%" }} />
        </Form.Item>
      ) : (
        children
      )}
    </td>
  );
};

const statusDescriptions = {
  authorised: "Invoice has been authorised successfully.",
  declined: "Invoice has been declined.",
  pending: "Invoice has been moved to pending for further review."
};

function DataTable({
  pageSize = 30,
  height = 230,
  data = [],
  loading = false,
  type = null
}) {
  const [searchRecord, setSearchRecord ] = useRecoilState(speciesSearchAtom)
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [editingKey, setEditingKey] = useState("");
  const [originalRow, setOriginalRow] = useState(null);
  const [editedRow, setEditedRow] = useState({});
  const { user_id } = User()
  const [tableForm] = Form.useForm();

  const isEditing = (record) => record.id === editingKey;  
  const [updateInvoice] = useMutation(UPDATE_INVOICE);
  const [insertInvoiceHistory] = useMutation(INSERT_INVOICE_HISTORY);

  const edit = (record) => {
    setEditingKey(record.id);  
    tableForm.setFieldsValue({
      total_value: record.total_value,
      adjustment: record.adjustment,
      payment_value: record.payment_value
    });

    setOriginalRow(record);
  };

  const cancel = () => {
    setEditingKey("");
    setOriginalRow(null);
  };

  // SINGLE ROW
  const handleSingleUpdate = (id, status) => {
    const values = tableForm.getFieldsValue(true);    
    const hasValueChanges =
      values?.total_value != null ||
      values?.adjustment != null ||
      values?.payment_value != null;

    const statusUpdate = hasValueChanges ? "pending" : status

    const set = {
      status: statusUpdate,
      updated_by: parseInt(user_id)
    };

    if (values?.total_value != null) set.total_value = values.total_value;
    if (values?.adjustment != null) set.adjustment = values.adjustment;
    if (values?.payment_value != null) set.payment_value = values.payment_value;

    updateInvoice({
      variables: {
        ids: [id],
        set
      }
    });

    if (statusUpdate === "authorised") {
      insertInvoiceHistory({
        variables: {
          objects: [{
            invoice_id: id,
            action: "Submitted for Approval",
            admin_user_id: parseInt(user_id),
            comment: "Transaction submitted for approval workflow.",
          }],
        },
      });
    }

    notification.success({
      message: "Saved!",
      description: statusDescriptions[statusUpdate] || "Invoice updated successfully."
    });
  };

  // BULK
  const handleBulkUpdate = (status) => {
    const set = {
      status: status,
      updated_by: parseInt(user_id)
    };
    updateInvoice({
      variables: {
        ids: selectedRowKeys,
        set
      }
    });
    notification.success({
      message: "Saved!",
      description: statusDescriptions[status] || "Invoice updated successfully."
    });
    setSelectedRowKeys([]);
  };

  const filterData = (data) => {  
    if(!searchRecord){
        return data
    }
    return _.filter(searchable, function (d) {
      let query = searchRecord.toLowerCase();
      return _.includes(d.searchText.toLowerCase(), query)
    });
  }

  const RowActions = ({ record, type, tableForm, originalRow, cancel, edit, handleSingleUpdate, isEditing }) => {
    const editing = isEditing(record);    
    const values = tableForm.getFieldsValue(true);

    const changed =
      editing &&
      values &&
      (
        values.total_value !== originalRow?.total_value ||
        values.adjustment !== originalRow?.adjustment ||
        values.payment_value !== originalRow?.payment_value
      );    

    return (
      <Space>
        <ActionDropdown
          id={record.status}
          submitLabel={changed ? "Resubmit" : "Submit"}
          dropdownDisabled={record.status !== "pending"}  
          onSubmit={(val) => {
            handleSingleUpdate(record.id, val);
            if (changed) {
              cancel(); 
              tableForm.resetFields();
            }
          }}
        />
      </Space>
    );
  };

  const editColumn = {
    title: "",
    width: 50,
    render: (_, record) => {
      const editing = isEditing(record);

      return editing ? (
        <Button style={{ borderColor: 'red'}} icon={<CloseOutlined style={{ color: 'red'}}/>} onClick={cancel} />
      ) : (
        <Button style={{ borderColor: THEME.colors.primary}} icon={<EditOutlined style={{ color: THEME.colors.primary}}/>} onClick={() => edit(record)} />
      );
    }
  };

  const columns = [
    {
      title: "Invoice",
      dataIndex: "id",
      key: "id",
      width: 90,
      render: (id) => <span>{leadingZeroes(id, appConstant.leadingZero)}</span>
    },
    {
      title: "Date",
      key: "date",
      dataIndex: 'created_at',
      render: (date) => <span>{moment(date).format("DD-MM-YY")}</span>
    },
    {
      title: "Auction ID",
      dataIndex: "purchase_orders",
      key: "auctionId",
      render: (po) => <span>{po[0].bid_id}</span>,
      width: 110
    },
    {
      title: "Created",
      dataIndex: 'created_at',
      key: "created",
      render: (date) => <span>{moment(date).format("HH:mm:ss")}</span>
    },
    {
      title: "Lot No.",
      dataIndex: "purchase_orders",
      key: "lotNo",
      width: 90,
      render: (po) => <span>{leadingZeroes(po[0].id, appConstant.leadingZero)}</span>
    },
    {
      title: "Vendor ID",
      dataIndex: "user_id",
      key: "vendorId",
      width: 100
    },
    {
      title: "Location",
      dataIndex: "location",
      key: "location",
      render: (_, record) => <span>{record.users?.user_location?.label ?? record.location_id}</span>
      
    },
    {
      title: "Total Value",
      dataIndex: "total_value",
      key: "total_value",
      width: 110,
      editable: true
    },
    {
      title: "Adjustment",
      dataIndex: "adjustment",
      key: "adjustment",
      width: 120,
      editable: true
    },
    {
      title: "Payment Value",
      dataIndex: "payment_value",
      key: "payment_value",
      width: 140,
      editable: true
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => {
      const isPendingAuth = status === PaymentStatus.PENDINGAUTHORISATION;

      return (
        <Tag color={["declined", "rejected"].includes(status?.toLowerCase()) ? "red" : "green"}>
          {isPendingAuth ? "PENDING AUTHORISATION" : status?.toUpperCase()}
        </Tag>
      );
    }
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <RowActions
          record={record}
          type={type}
          tableForm={tableForm}
          originalRow={originalRow}
          cancel={cancel}
          edit={edit}
          handleSingleUpdate={handleSingleUpdate}
          isEditing={isEditing}
        />
      )
    },
    ...(type === "declined" ? [editColumn] : []),
    {
      title: "User",
      dataIndex: "updated_by_admin",
      key: "user",
      render: (user) => (
        <span>{user ? user.full_name : "System Generated"}</span>
      )
    }
  ];

  const searchable = data.map((obj, i) => {
    let result = [];
    let concatenatedValues = "";
    columns.forEach(column => {
      if (obj[column.key] && obj[column.key] !== undefined) concatenatedValues += obj[column.key] + " ";
    });
    result.push(concatenatedValues.trim());

    return {
      key: i,
      ...obj,
      searchText: `${result.toString()}`
    }
  })

  const mergedColumns = columns.map(col => {
    if (!col.editable) return col;

    return {
      ...col,
      onCell: (record) => ({
        record,
        dataIndex: col.dataIndex,
        editable: col.editable,
        editing: isEditing(record)
      })
    };
  });

  return (
    <Container>
      <TableFilter data={filterData(data)} columns={columns} isBulk disabled={!selectedRowKeys.length} onSubmit={handleBulkUpdate}/>
      <Form 
        form={tableForm} 
        component={false} 
        onValuesChange={() => {
          const all = tableForm.getFieldsValue();
          setEditedRow(all);
        }}
      >
        <Table
          loading={loading}
          columns={mergedColumns}
          dataSource={filterData(data)}
          pagination={{
            defaultPageSize: pageSize,
          }}
          sticky
          scroll={{
            x: "max-content",
            y: "calc(100vh - 300px)"
          }}
          rowSelection={
            type === "pending"
              ? {
                  selectedRowKeys,
                  onChange: setSelectedRowKeys
                } : undefined
          }
          rowKey={rec => parseInt(rec.id, 10)}
          components={{
            body: {
              row: EditableRow,
              cell: EditableCell
            }
          }}
        />
      </Form>
    </Container>
  );
}

DataTable.propTypes = {
  pageSize: PropTypes.oneOf([10, 20, 50, 100]),
  height: PropTypes.number,
  data: PropTypes.arrayOf(PropTypes.object),
  loading: PropTypes.bool,
};

export default DataTable;
